import { createTheme } from '@mui/material/styles';
import { brandTokens } from '@softwareiq/brand-ui';
import { WORKSPACE_CONTENT_MAX_WIDTH } from '../common/AnalyticsWorkspace/workspaceStyles';

const lightTheme = createTheme({
  typography: {
    fontFamily: brandTokens.fontFamily,
  },
  palette: {
    mode: 'light',
    primary: {
      main: '#2952a3',
    },
    secondary: {
      main: '#0f8a6a',
    },
    background: {
      default: '#f5f6fb',
      paper: '#ffffff',
    },
    divider: 'rgba(44, 70, 122, 0.14)',
    action:{
      selected:'rgba(41, 82, 163, 0.10)'
    },
    text: {
      primary: '#102235',
      secondary: '#59657c',
      green:'rgb(0, 84, 0)',
      red:'rgb(84, 0, 0)',
      invGreen:'rgb(78, 216, 78)',
      invRed:'rgb(255, 93, 93)',
      // IB-specific colors
      inputBlue: 'rgb(0, 51, 153)', // Historical/input data (IB blue)
      calculated: 'rgb(0, 0, 0)', // Calculated values (black)
      negativeRed: 'rgb(153, 0, 0)', // Negative values (IB red)
    }
  },
  button: {
    backgroundColor: '#2196F3', // Default button background color in light theme
    color: '#2952a3',
  },
  toggleButton: {
    selectedTextColor: 'rgb(250,250,254)',
    selectedBackgroundColor: 'rgba(41, 82, 163, 0.10)',
  },
  table:{
    header:'rgba(255, 255, 255, 0.92)',
    agGridHeader: '#e5ebfa',
    agGridHeaderText: '#102235',
  },
  tableHeader:{
    highlightDark:'rgba(41, 82, 163, 0.12)',
    highlightGrey:'rgba(41, 82, 163, 0.08)',
    color:'#f7f9ff',
    stripe:'#f4f7fc',
    group:'rgba(247, 249, 255, 0.90)',
    highlight:'#f7f9ff',
    // IB-specific backgrounds
    sectionHeader: '#eaf1ff',
    subtotalBg: '#edf1f8',
    subtotalBorder: '#b8c4d8',
  },
  tooltip:{
    color:'rgb(232,232,236)',
  },
  background:{
    default: '#f5f6fb',
    footer: '#e5ebfa',
    backgroundColor:'#f5f6fb',
    tooltip: 'rgb(58,58,66,.82)', // Default background color for light mode
    transparent:'rgba(255, 255, 255, 0.92)',
    transparentDark:'rgba(41, 82, 163, 0.025)',
    
  },
  workspace: {
    surface: '#f5f6fb',
    rail: '#e5ebfa',
    toolbar: '#e5ebfa',
    header: '#e5ebfa',
    muted: '#f7f9ff',
    selected: 'rgba(41, 82, 163, 0.10)',
    hover: 'rgba(41, 82, 163, 0.06)',
    radius: 10,
    controlHeight: 30,
    outerInset: {
      compact: { xs: 1, sm: 1.5, md: 2 },
      comfortable: { xs: 1, sm: 1.5, md: 2 },
    },
    gutter: {
      compact: { xs: 1, sm: 1.5, md: 2 },
      comfortable: { xs: 1, sm: 1.5, md: 2 },
    },
    gap: {
      compact: { xs: 0.75, md: 1 },
      comfortable: { xs: 1, md: 1.5 },
    },
    contentMaxWidth: WORKSPACE_CONTENT_MAX_WIDTH,
    objectMaxWidth: {
      chart: { xs: '100%', md: 'min(100%, 1300px)' },
      table: { xs: '100%', md: 'min(100%, 1180px)' },
      full: '100%',
    },
    objectMinHeight: {
      chart: { xs: 300, md: 340 },
      table: { xs: 260, md: 280 },
    },
    splitRatios: {
      chartTable: 'minmax(320px, 44fr) minmax(260px, 56fr)',
      primaryChart: 'minmax(360px, 60fr) minmax(220px, 40fr)',
      even: 'minmax(280px, 1fr) minmax(280px, 1fr)',
    },
  },
  chrome: {
    pageBackground: '#f5f6fb',
    headerSurface: 'rgba(255, 255, 255, 0.50)',
    headerBorder: 'rgba(255, 255, 255, 0.55)',
    headerShadow: '0 14px 34px rgba(35, 55, 108, 0.08)',
    menuSurface: 'rgba(255, 255, 255, 0.92)',
    menuContentSurface: 'rgba(247, 249, 255, 0.90)',
    menuShadow: '0 14px 34px rgba(35, 55, 108, 0.14)',
    focusRing: 'rgba(41, 82, 163, 0.16)',
    fontFamily: brandTokens.fontFamily,
  },
  scrollbar: {
    width: '0.6em',
    height: '0.6em',
    backgroundColor: 'rgba(0,0,0,.3)',
    borderRadius: '10px',
  },
  components: {
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
          backgroundColor: 'rgba(41, 82, 163, 0.10)',
           },
          '&:hover': {
            backgroundColor: 'rgba(41, 82, 163, 0.08)',
          },
          '&.Mui-selected:hover': {
            backgroundColor: 'rgba(41, 82, 163, 0.14)',
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          color:'#59657c',
          '&.Mui-selected': {
          backgroundColor: 'rgba(41, 82, 163, 0.10)',
          color: '#2952a3',
           },
          '&:hover': {
            backgroundColor: 'rgba(41, 82, 163, 0.08)',
          },
          '&.Mui-selected:hover': {
            backgroundColor: 'rgba(41, 82, 163, 0.14)',
          },

        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.3s ease',
          '&:hover': {
            backgroundColor: 'rgba(41, 82, 163, 0.08)',
          },
        },
      },},
  },
});

const darkTheme = createTheme({
  typography: {
    fontFamily: brandTokens.fontFamily,
  },
  palette: {
    mode: 'dark',
    primary: {
      main: 'rgb(145,166,205)',
    },
    secondary: {
      main: 'rgb(145,166,205)',
    },
    action:{
      selected:'#3E4248'
    },
    text: {
      primary: '#FFF',
      secondary: '#AAA',
      green:'rgb(78, 216, 78)',
      red:'rgb(255, 93, 93)',
      invGreen:'rgb(0, 84, 0)',
      invRed:'rgb(84, 0, 0)',
      // IB-specific colors
      inputBlue: 'rgb(130, 170, 220)', // Historical/input data (IB blue)
      calculated: 'rgb(255, 255, 255)', // Calculated values (white)
      negativeRed: 'rgb(255, 120, 120)', // Negative values (IB red)
    },
  },
  table:{
    header:'rgb(26,26,30)',
    agGridHeader: 'rgb(26,26,30)',
    agGridHeaderText: '#AAA',
  },
  button: {
    backgroundColor: '#1976D2',
    color: 'rgb(145,166,205)'
  },
  toggleButton: {
    selectedTextColor: 'rgb(145,166,205)',
    selectedBackgroundColor: '#2C3443',
  },
  tooltip:{
    color:'rgb(30,30,38)',
  },
  tableHeader:{
    highlightDark:'rgb(50,50,90)',
    highlightGrey:'rgb(39, 39, 62)',
    color:'rgb(30,30,36)',
    stripe:'rgb(34,34,43)',
    group:'rgb(42,42,50,.9)',
    highlight:'rgb(39,39,50)',
    // IB-specific backgrounds
    sectionHeader: 'rgb(35, 40, 55)',
    subtotalBg: 'rgb(45, 45, 60)',
    subtotalBorder: 'rgb(80, 80, 110)',
  },
  background:{
    default: '#0e1218',
    backgroundColor:'#0e1218',
    footer:'rgb(26, 26, 34,.93)' ,
    tooltip: 'rgb(238,238,242,.82)', // Default background color for light mode
    transparent:'rgba(14, 18, 24, 0.92)',
    transparentDark:'rgb(0, 0, 255,.03)',
  },
  workspace: {
    surface: '#0e1218',
    rail: '#0a121f',
    toolbar: '#0a121f',
    header: '#0a121f',
    muted: 'rgb(39,39,50)',
    selected: 'rgba(145, 166, 205, 0.2)',
    hover: 'rgba(145, 166, 205, 0.18)',
    radius: 10,
    controlHeight: 30,
    outerInset: {
      compact: { xs: 1, sm: 1.5, md: 2 },
      comfortable: { xs: 1, sm: 1.5, md: 2 },
    },
    gutter: {
      compact: { xs: 1, sm: 1.5, md: 2 },
      comfortable: { xs: 1, sm: 1.5, md: 2 },
    },
    gap: {
      compact: { xs: 0.75, md: 1 },
      comfortable: { xs: 1, md: 1.5 },
    },
    contentMaxWidth: WORKSPACE_CONTENT_MAX_WIDTH,
    objectMaxWidth: {
      chart: { xs: '100%', md: 'min(100%, 1300px)' },
      table: { xs: '100%', md: 'min(100%, 1180px)' },
      full: '100%',
    },
    objectMinHeight: {
      chart: { xs: 300, md: 340 },
      table: { xs: 260, md: 280 },
    },
    splitRatios: {
      chartTable: 'minmax(320px, 44fr) minmax(260px, 56fr)',
      primaryChart: 'minmax(360px, 60fr) minmax(220px, 40fr)',
      even: 'minmax(280px, 1fr) minmax(280px, 1fr)',
    },
  },
  controls: {
    surface: 'rgba(75, 104, 158, 0.16)',
    hover: 'rgba(75, 104, 158, 0.24)',
    border: 'rgba(145, 166, 205, 0.28)',
  },
  chrome: {
    pageBackground: '#09111f',
    headerBackdrop: '#12213f',
    headerSurface: 'rgba(18, 33, 63, 0.72)',
    headerBorder: 'rgba(124, 158, 224, 0.16)',
    headerShadow: '0 14px 34px rgba(0, 0, 0, 0.24)',
    menuSurface: 'rgba(19, 31, 56, 0.94)',
    menuContentSurface: 'rgba(18, 33, 63, 0.90)',
    menuShadow: '0 14px 34px rgba(0, 0, 0, 0.30)',
    focusRing: 'rgba(124, 167, 255, 0.22)',
    fontFamily: brandTokens.fontFamily,
  },
  scrollbar: {
    width: '0.6em',
    height: '0.6em',
    backgroundColor: 'rgba(0,0,0,.3)',
    borderRadius: '10px',
  },
  components: {
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
          backgroundColor: 'rgb(50, 50, 90,.5)',
           },
          '&:hover': {
            backgroundColor: 'rgb(50, 50, 90,.8)',
          },
          '&.Mui-selected:hover': {
            backgroundColor: 'rgb(50, 50, 90,.8)',
          },
        },
      },
    },
    
    

    MuiToggleButton: {
      styleOverrides: {
        root: {
          color:'#AAA',
          '&.Mui-selected': {
            backgroundColor: 'rgb(50, 50, 90,.7)',
            color: 'rgb(145,166,205)',
          },
          '&:hover': {
            backgroundColor: 'rgb(50, 50, 90)',
          },
          '&.Mui-selected:hover': {
            backgroundColor: 'rgb(50, 50, 90)',
          },

        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.3s ease',
          '&:hover': {
            backgroundColor: 'rgb(50, 50, 90,.6)',
          },
        },
      },},
  
  },
});

export { lightTheme, darkTheme };
